import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CalendarDays, MapPin, Clock, Download, Eye, RefreshCw, CheckCircle2, Hourglass } from "lucide-react";

function fmtLong(d) {
  if (!d) return "";
  return new Date(d + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function downloadIcs(booking) {
  const ev = booking.event || {};
  const stamp = (d, t) => `${(d || "").replace(/-/g, "")}T${(t || "09:00").replace(":", "")}00`;
  const ics = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Lomond Operator Academy//EN", "BEGIN:VEVENT",
    `UID:${booking.id}@lomond-operator-academy`,
    `DTSTART:${stamp(ev.date, ev.start_time)}`,
    `DTEND:${stamp(ev.date, ev.end_time)}`,
    `SUMMARY:${ev.title || "Lomond Assessment Day"}`,
    `LOCATION:${[ev.location_name, ev.address].filter(Boolean).join(", ")}`,
    `DESCRIPTION:Please arrive ${ev.arrival_minutes || 15} minutes early. Contact ${ev.contact_name || "Lomond Academy"} ${ev.contact_number || ""}`,
    "END:VEVENT", "END:VCALENDAR",
  ].join("\r\n");
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "lomond-assessment-day.ics";
  a.click();
  URL.revokeObjectURL(url);
}

export default function AssessmentBooking() {
  const [data, setData] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/calendar/my").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data || (!data.invited && !data.booking)) return null;

  const booking = data.booking;
  const ev = booking?.event || {};

  const act = async (fn, key) => {
    setBusy(key);
    try {
      await fn();
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const book = (eventId) => act(() => api.post("/calendar/book", { event_id: eventId }), eventId);
  const reschedule = (eventId) => act(async () => {
    await api.post("/calendar/reschedule", { event_id: eventId });
    setRescheduling(false);
    toast.success("Your assessment day has been moved. Confirmation is in your messages.");
  }, eventId);
  const waitlist = (eventId) => act(async () => {
    await api.post("/calendar/waitlist", { event_id: eventId });
    toast.success("You're on the waiting list — we'll be in touch if a place opens.");
  }, eventId);

  const EventCard = ({ e, actionLabel, onAction, isReschedule }) => (
    <div className="bg-white border border-steel rounded-md p-5 flex flex-wrap items-center gap-4" data-testid={`event-card-${e.id}`}>
      <div className="bg-lomond rounded-md px-3 py-2 text-center min-w-16">
        <p className="font-heading font-black text-black text-xl leading-none">{new Date(e.date + "T00:00:00").getDate()}</p>
        <p className="font-mono text-[9px] uppercase tracking-widest text-black/70">{new Date(e.date + "T00:00:00").toLocaleDateString("en-GB", { month: "short" })}</p>
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-heading font-bold uppercase text-charcoal">{fmtLong(e.date)}</p>
        <p className="text-xs text-slate-500">{e.start_time}–{e.end_time} — {e.location_name}</p>
        <p className={`text-xs font-mono uppercase tracking-widest mt-1 ${e.spaces_remaining === 0 ? "text-amber-700" : "text-emerald-700"}`} data-testid={`spaces-${e.id}`}>
          {e.spaces_remaining === 0 ? "Assessment day full" : `${e.spaces_remaining} space${e.spaces_remaining === 1 ? "" : "s"} remaining`}
        </p>
      </div>
      {e.spaces_remaining > 0 ? (
        <Button onClick={() => onAction(e.id)} disabled={busy === e.id}
          className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid={`${isReschedule ? "reschedule" : "book"}-btn-${e.id}`}>
          {busy === e.id ? "..." : actionLabel}
        </Button>
      ) : !isReschedule && (
        <Button variant="outline" onClick={() => waitlist(e.id)} disabled={busy === e.id}
          className="border-charcoal/30 text-charcoal hover:border-lomond" data-testid={`waitlist-btn-${e.id}`}>
          {busy === e.id ? "..." : "Join Waiting List"}
        </Button>
      )}
    </div>
  );

  // Offered a place from waiting list / admin invite to a specific event
  if (booking?.status === "invited") {
    return (
      <section className="bg-white border-2 border-lomond rounded-md p-6" data-testid="offered-place-card">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lomond mb-2">A place is being held for you</p>
        <h3 className="font-heading font-bold uppercase text-charcoal text-lg">{fmtLong(booking.event_date)}</h3>
        <p className="text-sm text-slate-500 mt-1">{booking.event_start_time} — {booking.event_location}</p>
        <Button onClick={() => book(booking.event_id)} disabled={busy}
          className="mt-4 bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="confirm-place-btn">
          <CheckCircle2 className="h-4 w-4 mr-1.5" /> Confirm My Place
        </Button>
      </section>
    );
  }

  // Confirmed booking
  if (["booked", "arrived"].includes(booking?.status)) {
    return (
      <section className="bg-white border border-steel rounded-md overflow-hidden" data-testid="booking-section">
        <div className="bg-charcoal text-white px-6 py-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lomond">Assessment Day Confirmed</p>
          <h3 className="font-heading font-extrabold uppercase text-xl mt-1" data-testid="booking-confirmed-heading">
            {fmtLong(booking.event_date)}
          </h3>
        </div>
        <div className="p-6 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <p className="flex items-center gap-2 text-charcoal"><Clock className="h-4 w-4 text-lomond" /> <span data-testid="booking-time">{booking.event_start_time} start — arrive {ev.arrival_minutes || 15} minutes early</span></p>
            <p className="flex items-center gap-2 text-charcoal"><MapPin className="h-4 w-4 text-lomond" /> <span data-testid="booking-location">{ev.location_name}</span></p>
            {ev.address && <p className="text-xs text-slate-500 sm:col-span-2" data-testid="booking-address">{ev.address}</p>}
            {ev.contact_name && <p className="text-xs text-slate-500 sm:col-span-2" data-testid="booking-contact">Contact: {ev.contact_name} — {ev.contact_number}</p>}
          </div>
          {detailsOpen && (
            <div className="bg-elevated rounded-md p-4 space-y-3 text-sm" data-testid="booking-details-panel">
              {[["What to bring", ev.what_to_bring], ["PPE / clothing", ev.ppe_requirements], ["Parking", ev.parking_instructions], ["Travel", ev.travel_info], ["Instructions", ev.candidate_instructions]]
                .filter(([, v]) => v).map(([l, v]) => (
                  <div key={l}><p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{l}</p><p className="text-charcoal mt-0.5">{v}</p></div>
                ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => downloadIcs(booking)} className="border-steel text-charcoal bg-white" data-testid="add-to-calendar-btn">
              <Download className="h-4 w-4 mr-1.5" /> Add to Calendar
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDetailsOpen(!detailsOpen)} className="border-steel text-charcoal bg-white" data-testid="view-details-btn">
              <Eye className="h-4 w-4 mr-1.5" /> {detailsOpen ? "Hide Details" : "View Details"}
            </Button>
            {data.self_reschedule_enabled && (
              <Button variant="outline" size="sm" onClick={() => setRescheduling(!rescheduling)} className="border-steel text-charcoal bg-white" data-testid="request-reschedule-btn">
                <RefreshCw className="h-4 w-4 mr-1.5" /> Request Another Date
              </Button>
            )}
          </div>
          {rescheduling && (
            <div className="space-y-3 pt-3 border-t border-steel" data-testid="reschedule-picker">
              <p className="text-xs text-slate-500">Choose a new date — your current slot is released immediately. Self-rescheduling closes {data.reschedule_cutoff_hours}h before your assessment.</p>
              {data.available_events.filter((e) => e.id !== booking.event_id).map((e) => (
                <EventCard key={e.id} e={e} actionLabel="Move to This Date" onAction={reschedule} isReschedule />
              ))}
              {!data.available_events.filter((e) => e.id !== booking.event_id).length && (
                <p className="text-sm text-slate-500">No other dates available right now — please contact the Academy.</p>
              )}
            </div>
          )}
        </div>
      </section>
    );
  }

  if (booking?.status === "waiting") {
    return (
      <section className="bg-white border border-amber-300 rounded-md p-6" data-testid="waiting-list-card">
        <div className="flex items-start gap-3">
          <Hourglass className="h-5 w-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="font-heading font-bold uppercase text-charcoal">Waiting list — {fmtLong(booking.event_date)}</h3>
            <p className="text-sm text-slate-500 mt-1">That assessment day is full. We'll message you here the moment a place opens up. You can also book a different date below.</p>
          </div>
        </div>
        <div className="space-y-3 mt-4">
          {data.available_events.filter((e) => e.spaces_remaining > 0).map((e) => (
            <EventCard key={e.id} e={e} actionLabel="Book This Date" onAction={book} />
          ))}
        </div>
      </section>
    );
  }

  // Invited, not yet booked
  if (data.invited) {
    return (
      <section className="bg-white border-2 border-lomond rounded-md p-6" data-testid="booking-section">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lomond mb-1">You're invited</p>
        <h3 className="font-heading font-extrabold uppercase text-charcoal text-xl" data-testid="book-assessment-heading">
          <CalendarDays className="inline h-5 w-5 mr-2 -mt-1" />Book Your Assessment Day
        </h3>
        <p className="text-sm text-slate-500 mt-1 mb-4">Choose a date that suits you. Interview, simulator and practical aptitude all happen on the same day.</p>
        <div className="space-y-3">
          {data.available_events.map((e) => <EventCard key={e.id} e={e} actionLabel="Book This Date" onAction={book} />)}
          {!data.available_events.length && <p className="text-sm text-slate-500">No dates open for booking right now — we'll message you as soon as new dates are added.</p>}
        </div>
      </section>
    );
  }

  return null;
}
